import type { Volume } from '../types/content'

export const volumes: Volume[] = [
  {
    number: 'Volume 1',
    title: 'First 20 Swim Lessons: Beginner Foundations & Water Confidence',
    lessons: '20 Lessons',
    subtitle: 'The structured starting point every new swimmer needs',
    intro:
      'Teaching a beginner is about much more than getting them to move through the water. Before stroke technique, swimmers need confidence, body awareness, basic control, and a progressive introduction to the aquatic environment. This volume gives you 20 complete ready-to-teach lessons designed specifically for those first stages.',
    bulletsTitle: 'Inside you’ll learn how to teach',
    bullets: [
      'Water confidence and gradual adaptation',
      'Safe and controlled water entry',
      'Comfortable movement in the pool and basic body awareness',
      'Early submersion skills and orientation in the water',
      'Supported-to-independent movement progressions',
      'Basic recovery positions and early floating preparation',
      'Introduction to breathing control and confidence-building progressions',
      'Fundamental skills that prepare swimmers for later stroke development',
    ],
    problem:
      'Instead of arriving at a beginner lesson asking “What should I teach next?”, you have a clear sequence that builds the swimmer’s foundations one step at a time.',
    result:
      'Your beginner swimmers develop greater confidence, control, and independence while you teach from a clear structure instead of improvising every session.',
    popular: true,
  },
  {
    number: 'Volume 2',
    title: 'Floating & Breathing: 15 Ready-to-Teach Lessons',
    lessons: '15 Lessons',
    subtitle: 'Build the control and body position that every stroke depends on',
    intro:
      'Breathing and floating problems can affect almost every skill that comes afterward. A swimmer who is tense, uncomfortable with exhalation, or unable to maintain a stable position will often struggle when stroke technique becomes more demanding. This volume gives you 15 structured lessons focused specifically on these foundations.',
    bulletsTitle: 'You’ll work on',
    bullets: [
      'Relaxed breathing patterns and controlled underwater exhalation',
      'Face-in-water confidence',
      'Front floating, back floating, and recovery from floating positions',
      'Body alignment and balance in the water',
      'Streamlined positioning and basic gliding',
      'Transitions between positions',
      'Progressive reduction of instructor support',
      'Greater independence and control',
    ],
    problem:
      'You no longer have to keep telling a swimmer to “relax” without a clear progression to help them do it. You get practical steps for developing breathing control, floating, balance, and body position gradually.',
    result:
      'Your swimmers develop stronger aquatic foundations and are better prepared to progress into kicking and complete stroke development.',
  },
  {
    number: 'Volume 3',
    title: 'Freestyle: 20 Ready-to-Teach Lessons',
    lessons: '20 Lessons',
    subtitle: 'Teach freestyle as a progression — not as one complicated movement',
    intro:
      'Freestyle becomes much easier to teach when the swimmer does not have to learn body position, kicking, arms, rotation, and breathing all at once. This volume gives you 20 complete freestyle lessons that break the stroke into teachable components before progressively combining them.',
    bulletsTitle: 'You’ll teach',
    bullets: [
      'Streamlined body position and flutter kicking',
      'Propulsion, hand entry, arm action, and recovery',
      'Body rotation and side breathing',
      'Arm-and-breath coordination and stroke timing',
      'Complete-stroke integration, rhythm, and efficiency',
      'Common freestyle corrections',
      'Progressive distance development',
      'Maintaining technique as distance increases',
    ],
    problem:
      'Instead of repeatedly saying “try the full stroke again”, you can identify which component needs attention and select the appropriate progression.',
    result:
      'You teach freestyle with a more logical progression, making it easier to identify problems and build the complete stroke step by step.',
  },
  {
    number: 'Volume 4',
    title: 'Backstroke, Breaststroke & Butterfly: 25 Ready-to-Teach Lessons',
    lessons: '25 Lessons',
    subtitle: 'Three major strokes. One structured teaching resource.',
    intro:
      'Backstroke, breaststroke, and butterfly each require different timing, coordination, and movement patterns. Trying to teach the entire stroke at once often creates confusion. This volume gives you 25 ready-to-teach lessons that help break each stroke into manageable components.',
    bulletsTitle: 'You’ll work on',
    bullets: [
      'Backstroke — back body position, alignment, kick, rotation, arm action, hand entry, timing, coordination, and complete-stroke integration',
      'Breaststroke — body position, leg action, kick recovery, arm action, breathing, timing, pull-kick coordination, glide, and complete-stroke integration',
      'Butterfly — body undulation, dolphin kick, rhythm, arm action, recovery, breathing, timing, coordination, and progressive full-stroke development',
    ],
    problem:
      'You get a step-by-step path for teaching strokes that can become frustrating when too many technical elements are introduced together.',
    result:
      'You can break complex strokes into smaller teaching objectives and progressively build toward coordinated swimming.',
  },
  {
    number: 'Volume 5',
    title: 'Mixed-Level Groups & Class Management: 20 Ready-to-Teach Lessons',
    lessons: '20 Lessons',
    subtitle: 'Because real swimming classes rarely contain identical swimmers',
    intro:
      'One swimmer is ready to progress. Another needs an easier version. Someone else finishes early. And you still need to keep the entire group moving, supervised, and engaged. This volume contains 20 ready-to-teach lessons designed specifically around real class-management challenges.',
    bulletsTitle: 'You’ll learn how to organize',
    bullets: [
      'Mixed-level swimming groups and small-group lessons',
      'Skill stations, rotations, and pool-space distribution',
      'Different difficulty levels inside one activity',
      'Easy, standard, and advanced variations',
      'Active waiting strategies and efficient transitions',
      'Multiple swimmer needs in a single session',
      'Classes where swimmers progress at different speeds',
      'Limited lane space and more structured group supervision',
    ],
    problem:
      'You do not need to create a completely different lesson for every swimmer. You learn how to work from one objective while adapting difficulty and organization.',
    result:
      'Your classes become more organized, swimmers spend more time actively learning, and you have a clearer system for managing different ability levels.',
  },
  {
    number: 'Volume 6',
    title: 'Swim Conditioning, Endurance & Progressive Training: 20 Ready-to-Teach Lessons',
    lessons: '20 Lessons',
    subtitle: 'Take swimmers beyond basic skill acquisition',
    intro:
      'Once a swimmer can perform the core skills, the next challenge is increasing distance, consistency, efficiency, and endurance without allowing technique to fall apart. This volume includes 20 structured lessons for progressive swimming development.',
    bulletsTitle: 'You’ll work on',
    bullets: [
      'Increasing swimming distance gradually and endurance development',
      'Maintaining technique over longer efforts',
      'Consistent stroke mechanics and controlled swimming intensity',
      'Interval structures and recovery periods',
      'Multi-stroke sessions and progressive conditioning',
      'Efficiency, independence, and repeated technical performance',
      'More advanced session organization',
    ],
    problem:
      'You have a clear next step after basic stroke instruction instead of simply asking swimmers to complete more lengths.',
    result:
      'Your swimmers move from learning individual skills to developing more consistent, efficient, and sustained swimming.',
  },
]

export const volumeStages = [
  { number: 'Volume 1', stage: 'Build confidence and beginner foundations' },
  { number: 'Volume 2', stage: 'Develop breathing, floating and body control' },
  { number: 'Volume 3', stage: 'Build freestyle progressively' },
  { number: 'Volume 4', stage: 'Teach backstroke, breaststroke and butterfly' },
  { number: 'Volume 5', stage: 'Manage groups and mixed ability levels' },
  { number: 'Volume 6', stage: 'Develop endurance and progressive training' },
]
